import { useNavigation } from "@react-navigation/native";
import { Divider } from "@ui-kitten/components";
import Chip from "components/Chip";
import FilterIcon from "components/FilterIcon";
import dayjs from "dayjs";
import {
  setEventFilters,
  setIsChangingSeat,
  setOriginalEventId,
  setOriginalTickets,
  setSelectedEvent,
  setSelectedPartySize,
} from "features/slices/reservationFlowSlice";
import { EventAvailabilityDetails, EventType } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDeferredSeatAssignment } from "hooks/useDeferredSeatAssignment";
import useSubscriptionOperable from "hooks/useSubscriptionOperable";
import useTDCDialog from "hooks/useTDCDialog";
import i18n from "plugins/localization";
import { useFetchEventAvailablitiesQuery } from "queries/ticket.queries";
import React, { useEffect, useState } from "react";
import { ActivityIndicator, Text, View } from "react-native";
import toast from "utils/toast";
import Filters from "./components/Filters";
import RSVPDialog from "./components/RSVPDialog";
import TDCDialog from "./components/TDCDialog";
import ScheduleItemList from "./ScheduleItemList";

export const NoEventsText = () => (
  <Text className="text-3xl font-bold text-gray-400 text-center">
    {i18n.t("screens.Schedule.noEligibleEventsBanner")}
  </Text>
);

const MONTH_FILTER_FORMAT = "MMM";
const DAY_FILTER_FORMAT = "ddd";

const CapturedSchedule = () => {
  const { account } = useAccount();
  const dispatch = useAppDispatch();
  const navigation: any = useNavigation();

  const [emptySchedule, setEmptySchedule] = useState(false);
  const [toggled, setToggled] = useState(false);
  const {
    rsvpDialogVisible,
    setRsvpDialogVisible,
    rsvpEvent,
    handleDeferredSeatAssignment,
  } = useDeferredSeatAssignment();
  const { TDCDialogVisible, setTDCDialogVisible, withTDCCheck } =
    useTDCDialog();
  const { selectedSubscription, eventFilters, selectedEvent } = useAppSelector(
    (state) => state.reservationFlow,
  );

  if (!account) return null;

  const {
    data: schedule = [],
    isFetching,
    refetch: fetchEventAvailablities,
  } = useFetchEventAvailablitiesQuery({
    accountId: account.id,
    planPackageId: selectedSubscription?.planPackage.id,
  });

  const subscriptionOperative = useSubscriptionOperable(selectedSubscription);

  const filterSchedule = (schedule: EventAvailabilityDetails[]) => {
    const filterByEventType = (item: EventAvailabilityDetails) => {
      return eventFilters.eventTypes.length === 0
        ? true
        : eventFilters.eventTypes.includes(item.eventType);
    };

    const filterByMonths = (item: EventAvailabilityDetails) => {
      if (eventFilters.months.length === 0) return true;
      const itemMonth = dayjs(item.date)
        .tz(item.timezone)
        .format(MONTH_FILTER_FORMAT);
      return eventFilters.months.includes(itemMonth);
    };

    const filterByDay = (item: EventAvailabilityDetails) => {
      const itemDay = dayjs(item.date)
        .tz(item.timezone)
        .format(DAY_FILTER_FORMAT);
      return eventFilters.days.length === 0
        ? true
        : eventFilters.days.includes(itemDay);
    };

    return schedule
      .filter(filterByEventType)
      .filter(filterByMonths)
      .filter(filterByDay);
  };

  const onScheduleItemClick = (item: EventAvailabilityDetails) => {
    dispatch(setSelectedEvent(item));

    handleDeferredSeatAssignment(item, () => {
      withTDCCheck(item, () => {
        dispatch(setOriginalTickets([]));
        dispatch(setIsChangingSeat(false));
        dispatch(setOriginalEventId(undefined));
        dispatch(setSelectedPartySize(0));
        navigation.navigate("Schedule", {
          screen: "SeatSelection",
          initial: false,
        });
      });
    });
  };

  useEffect(() => {
    async function fetchSchedule() {
      if (!selectedSubscription || account === null) return;
      const { data: response = [] } = await fetchEventAvailablities();
      setEmptySchedule(response.length === 0);
    }
    // if selectedSubscription is undefined, then it means user has no valid subscription to be picked from,
    // the user has to add a membership
    // selectedSubscription is initialized to null
    if (selectedSubscription === undefined) {
      toast.error(i18n.t("screens.Schedule.noEligibleSubscriptions"));
      return;
    }
    fetchSchedule();
  }, [selectedSubscription, account]);

  useEffect(() => {
    if (!selectedSubscription || schedule.length === 0) return;
    const defaultFilter =
      selectedSubscription.planPackage.defaultEventTypeFilter;
    if (!defaultFilter) return;
    const scheduleEventTypes = new Set(schedule.map((e) => e.eventType));
    const defaultEventTypes = defaultFilter
      .split(",")
      .map((eventType) => eventType.trim())
      .filter((eventType) => scheduleEventTypes.has(eventType as EventType));
    if (defaultEventTypes.length > 0) {
      dispatch(
        setEventFilters({ eventTypes: defaultEventTypes, months: [], days: [] }),
      );
    }
  }, [schedule, selectedSubscription]);

  return (
    <View className="p-4 overflow-hidden">
      {selectedEvent && (
        <TDCDialog
          visible={TDCDialogVisible}
          setVisible={setTDCDialogVisible}
          cancelText="CLOSE"
          event={selectedEvent}
        />
      )}

      {rsvpEvent && selectedSubscription && (
        <RSVPDialog
          visible={rsvpDialogVisible}
          setVisible={setRsvpDialogVisible}
          event={rsvpEvent}
          planPackageId={selectedSubscription.planPackage.id}
          selectedSubscription={selectedSubscription}
        />
      )}

      <View className="mb-2 flex-row items-center justify-end gap-2">
        {emptySchedule && (
          <Text className="text-xl font-bold">
            {i18n.t("screens.Schedule.noEligibleEvents")}
          </Text>
        )}
        <Chip
          selected={eventFilters.eventTypes.includes("EXPERIENCE")}
          onPress={() =>
            dispatch(
              setEventFilters({
                ...eventFilters,
                eventTypes: eventFilters.eventTypes.includes("EXPERIENCE")
                  ? []
                  : ["EXPERIENCE"],
              }),
            )
          }
        >
          Show Experiences
        </Chip>
        <FilterIcon onPress={() => setToggled(!toggled)} />
      </View>
      <Divider />
      <Filters
        toggled={toggled}
        schedule={schedule}
        monthFormat={MONTH_FILTER_FORMAT}
        dayFormat={DAY_FILTER_FORMAT}
      />
      <View className="mt-2"></View>
      {emptySchedule && <NoEventsText />}
      {isFetching ? (
        <ActivityIndicator />
      ) : (
        <ScheduleItemList
          schedule={filterSchedule(schedule)}
          selectedSubscription={selectedSubscription}
          planPackageOperable={subscriptionOperative}
          onScheduleItemClick={onScheduleItemClick}
        />
      )}
    </View>
  );
};

export default CapturedSchedule;
